import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ArrowLeft, User, Mail, Phone, Building, MapPin, Calendar, Shield, FileText } from 'lucide-react';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { adminToken } = useAdminAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (adminToken && userId) {
      loadUser();
    }
  }, [adminToken, userId]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        console.error('Errore caricamento utente');
      }
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="text-center">
          <p className="text-gray-500">Utente non trovato</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 text-purple-600 hover:text-purple-800"
          >
            Torna alla lista
          </button>
        </div>
      </div>
    );
  }

  // L'indirizzo può essere una stringa (formato legacy clienti) o un oggetto
  // {street, city, province, zipcode, country} (agenti signup pubblico).
  const _addrObj = user.address && typeof user.address === 'object' ? user.address : null;
  const address = _addrObj ? _addrObj.street : (user.address || user.businessInfo?.address);
  const city = (_addrObj ? _addrObj.city : user.city) || user.businessInfo?.city;
  const zipCode = (_addrObj ? _addrObj.zipcode : (user.zipCode || user.zipcode)) || user.businessInfo?.zipCode;
  const province = (_addrObj ? _addrObj.province : user.province) || user.businessInfo?.province;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Torna alla lista utenti
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Dettaglio Utente
        </h1>
      </div>

      {/* Card principale */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header card con nome */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-4">
              <User className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-purple-100">
                {user.userType === 'business' ? '🏢 Azienda' : '👤 Privato'}
              </p>
            </div>
          </div>
        </div>

        {/* Contenuto */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Informazioni di contatto */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                📞 Informazioni di Contatto
              </h3>
              
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900 font-medium">{user.email}</p>
                  {user.emailVerified && (
                    <span className="text-xs text-green-600">✓ Verificata</span>
                  )}
                </div>
              </div>

              {user.phone && (
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Telefono</p>
                    <p className="text-gray-900 font-medium">{user.phone}</p>
                  </div>
                </div>
              )}

              {user.dateOfBirth && (
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Data di Nascita</p>
                    <p className="text-gray-900 font-medium">{user.dateOfBirth}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Indirizzo */}
            {(address || city || zipCode) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  📍 Indirizzo
                </h3>
                
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    {address && <p className="text-gray-900 font-medium">{address}</p>}
                    {(zipCode || city) && (
                      <p className="text-gray-700">
                        {zipCode} {city} {province && `(${province})`}
                      </p>
                    )}
                    {user.country && (
                      <p className="text-gray-500 text-sm">{user.country}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Indirizzo di Spedizione */}
            {user.customerAddresses?.shippingAddress && (() => {
              const shipping = user.customerAddresses.shippingAddress;
              const hasShippingData = shipping.address || shipping.city || shipping.postalCode;
              
              if (!hasShippingData) return null;
              
              return (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    🚚 Indirizzo di Spedizione
                  </h3>
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      {(shipping.firstName || shipping.lastName) && (
                        <p className="text-gray-900 font-semibold mb-1">
                          {shipping.firstName} {shipping.lastName}
                        </p>
                      )}
                      {shipping.company && (
                        <p className="text-gray-600 text-sm mb-1">{shipping.company}</p>
                      )}
                      {shipping.address && (
                        <p className="text-gray-900 font-medium">{shipping.address}</p>
                      )}
                      {(shipping.postalCode || shipping.city) && (
                        <p className="text-gray-700">
                          {shipping.postalCode} {shipping.city} {shipping.province && `(${shipping.province})`}
                        </p>
                      )}
                      {shipping.country && (
                        <p className="text-gray-500 text-sm">{shipping.country}</p>
                      )}
                      {shipping.phone && (
                        <p className="text-gray-600 text-sm mt-2">
                          📞 {shipping.phone}
                        </p>
                      )}
                      {shipping.notes && (
                        <p className="text-gray-500 text-sm italic mt-2">
                          Note: {shipping.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Info Azienda */}
            {user.userType === 'business' && user.businessInfo && (
              <div className="space-y-4 md:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  🏢 Informazioni Azienda
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {user.businessInfo.companyName && (
                    <div className="flex items-start space-x-3">
                      <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Ragione Sociale</p>
                        <p className="text-gray-900 font-medium">{user.businessInfo.companyName}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.vatNumber && (
                    <div className="flex items-start space-x-3">
                      <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Partita IVA</p>
                        <p className="text-gray-900 font-medium">{user.businessInfo.vatNumber}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.fiscalCode && (
                    <div className="flex items-start space-x-3">
                      <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Codice Fiscale</p>
                        <p className="text-gray-900 font-medium">{user.businessInfo.fiscalCode}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.businessType && (
                    <div className="flex items-start space-x-3">
                      <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Tipo Attività</p>
                        <p className="text-gray-900 font-medium capitalize">{user.businessInfo.businessType}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.pec && (
                    <div className="flex items-start space-x-3">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">PEC</p>
                        <p className="text-gray-900 font-medium">{user.businessInfo.pec}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.codiceUnivoco && (
                    <div className="flex items-start space-x-3">
                      <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Codice Univoco</p>
                        <p className="text-gray-900 font-medium">{user.businessInfo.codiceUnivoco}</p>
                      </div>
                    </div>
                  )}

                  {user.businessInfo.website && (
                    <div className="flex items-start space-x-3 md:col-span-2">
                      <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Sito Web</p>
                        <a 
                          href={user.businessInfo.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-800 font-medium"
                        >
                          {user.businessInfo.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stato CRM */}
            <div className="space-y-4 md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                💼 Stato CRM
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <div className={`w-3 h-3 rounded-full mt-1.5 ${user.crmEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div>
                    <p className="text-sm text-gray-500">Stato nel CRM</p>
                    <p className={`font-medium ${user.crmEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                      {user.crmEnabled ? '✓ Aggiunto al CRM' : 'Non nel CRM'}
                    </p>
                  </div>
                </div>

                {user.assignedTo && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-purple-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Assegnato a</p>
                      <p className="text-gray-900 font-medium">{user.assignedTo}</p>
                    </div>
                  </div>
                )}

                {user.crmNotes && user.crmNotes.length > 0 && (
                  <div className="flex items-start space-x-3 md:col-span-2">
                    <FileText className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 mb-2">Note CRM</p>
                      <div className="space-y-2">
                        {user.crmNotes.slice(0, 3).map((note, idx) => (
                          <div key={idx} className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                            <p className="text-sm text-gray-700">{note.content || note.note}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {note.createdAt && new Date(note.createdAt).toLocaleDateString('it-IT')}
                              {note.createdBy && ` - ${note.createdBy}`}
                            </p>
                          </div>
                        ))}
                        {user.crmNotes.length > 3 && (
                          <p className="text-sm text-gray-500 italic">
                            + altre {user.crmNotes.length - 3} note
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {user.tags && user.tags.length > 0 && (
                  <div className="flex items-start space-x-3 md:col-span-2">
                    <FileText className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Tag</p>
                      <div className="flex flex-wrap gap-2">
                        {user.tags.map((tag, idx) => (
                          <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Account */}
            <div className="space-y-4 md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                🔐 Informazioni Account
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Stato</p>
                    <p className={`font-medium ${user.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                      {user.status === 'active' ? 'Attivo' : 'Inattivo'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Registrato il</p>
                    <p className="text-gray-900 font-medium">
                      {new Date(user.createdAt).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>

                {user.lastLoginAt && (
                  <div className="flex items-start space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Ultimo accesso</p>
                      <p className="text-gray-900 font-medium">
                        {new Date(user.lastLoginAt).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Footer con azioni */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Torna alla Lista
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
